import { useEffect, useState } from "react";
import { getHandle, setHandle } from "../identity.js";

/**
 * Collect a display handle the first time a player posts to a leaderboard. No auth —
 * this is the only identity beyond the anon uuid. Client validation mirrors the
 * server's sanitizeHandle (1–24 chars); the server re-validates regardless. Shared by
 * every game.
 *
 * "connect X" is a shortcut, not a login: auth.karrtopelka.com runs one OAuth
 * round-trip, writes the @username into the shared identity cookie, and bounces back
 * here — the page reloads already knowing the handle, so this modal never re-opens.
 */

/** Only offer the X shortcut where the shared cookie can actually come back. */
function xConnectUrl(): string | null {
  if (typeof location === "undefined") return null;
  const host = location.hostname;
  if (host !== "karrtopelka.com" && !host.endsWith(".karrtopelka.com")) return null;
  return `https://auth.karrtopelka.com/login?return=${encodeURIComponent(location.href)}`;
}

export function HandleModal({
  onDone,
  onCancel,
}: {
  onDone: (handle: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState("");
  const [waitingForX, setWaitingForX] = useState(false);
  const trimmed = value.trim().replace(/\s+/g, " ");
  const valid = trimmed.length >= 1 && trimmed.length <= 24;
  const connectX = xConnectUrl();

  const confirm = () => {
    if (!valid) return;
    setHandle(trimmed);
    onDone(trimmed);
  };

  // X connect opens in a NEW tab so the finished run (score in the store) survives.
  // The cookie lands on .karrtopelka.com from that tab; here we poll for it and
  // continue the post automatically the moment it shows up.
  useEffect(() => {
    if (!waitingForX) return;
    const t = setInterval(() => {
      const h = getHandle();
      if (h) {
        clearInterval(t);
        onDone(h);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [waitingForX, onDone]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6">
      <div className="w-full max-w-sm rounded-lg border border-border bg-surface-raised p-6">
        <h2 className="text-xl font-bold text-text">pick a handle</h2>
        <p className="mt-1 text-sm text-text-muted">
          shows on the leaderboard + your card.
        </p>
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") confirm();
            if (e.key === "Escape") onCancel();
          }}
          maxLength={24}
          placeholder="your_handle"
          className="mt-4 w-full rounded-md border border-border bg-bg px-3 py-2 text-text outline-none focus:border-accent"
        />
        {connectX && (
          <a
            href={connectX}
            target="_blank"
            rel="noopener"
            onClick={() => setWaitingForX(true)}
            className="mt-3 block text-center text-sm text-text-muted underline-offset-2 hover:text-accent hover:underline"
          >
            {waitingForX
              ? "waiting for X… finish in the other tab"
              : "or connect X — use your real @handle"}
          </a>
        )}
        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md px-4 py-2 text-sm text-text-muted hover:text-text"
          >
            cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={!valid}
            className="rounded-md bg-accent px-4 py-2 text-sm font-semibold text-bg disabled:opacity-40"
          >
            save
          </button>
        </div>
      </div>
    </div>
  );
}
